import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import {
  CREATOR_CODE_WINDOW_DAYS,
  type CreatorCodeSurface,
} from "@shared/creatorCodes";
import "@/locales/registerCreatorCodeI18n";

type Translate = ReturnType<typeof useTranslation>["t"];

type RedeemResult =
  | {
      ok: true;
      code: string;
      bonusCredits: number;
      discountPercent: number | null;
      alreadyApplied: boolean;
    }
  | {
      ok: false;
      reason:
        | "unknown_code"
        | "too_late"
        | "other_code_applied"
        | "rate_limited"
        | "unavailable";
    };

const FAILURE_KEYS = {
  unknown_code: "unknownCode",
  too_late: "tooLate",
  other_code_applied: "otherCode",
  rate_limited: "rateLimited",
  unavailable: "error",
} as const;

/** The line shown under a field for a code redemption. */
function creatorCodeResultText(t: Translate, result: RedeemResult): string {
  if (!result.ok) {
    return t(`creatorCode.${FAILURE_KEYS[result.reason]}`, {
      days: CREATOR_CODE_WINDOW_DAYS,
    });
  }
  if (result.alreadyApplied) {
    return t("creatorCode.alreadyApplied", { code: result.code });
  }
  if (result.bonusCredits > 0) {
    return t("creatorCode.appliedBonus", {
      code: result.code,
      count: result.bonusCredits,
    });
  }
  if (result.discountPercent) {
    return t("creatorCode.appliedDiscount", {
      code: result.code,
      percent: result.discountPercent,
    });
  }
  return t("creatorCode.applied", { code: result.code });
}

type Message = { ok: boolean; text: string } | null;

/** One input + button row with an "Optional" pill, shared by both fields. */
function OptionalInputRow({
  value,
  onChange,
  placeholder,
  buttonLabel,
  pending,
  locked,
  autoFocus,
  onDark,
  uppercase,
  message,
  onSubmit,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  buttonLabel: string;
  pending: boolean;
  locked: boolean;
  autoFocus?: boolean;
  onDark?: boolean;
  uppercase?: boolean;
  message: Message;
  onSubmit: () => void;
}) {
  const { t } = useTranslation();
  const field = onDark
    ? "border-white/10 bg-white/[0.03] text-white placeholder:text-neutral-500 focus-within:border-indigo-400"
    : "border-border bg-background text-foreground placeholder:text-muted-foreground focus-within:border-primary";

  return (
    <div className="w-full">
      <form
        className="flex gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <label
          className={`relative flex flex-1 items-center rounded-lg border transition-colors ${field}`}
        >
          <input
            value={value}
            onChange={(e) =>
              onChange(
                uppercase ? e.target.value.toUpperCase() : e.target.value,
              )
            }
            autoFocus={autoFocus}
            disabled={locked}
            maxLength={uppercase ? 64 : 120}
            autoCapitalize={uppercase ? "characters" : "off"}
            autoComplete="off"
            spellCheck={false}
            aria-label={placeholder}
            placeholder={placeholder}
            className={`h-10 w-full bg-transparent px-3 text-sm outline-none ${
              uppercase
                ? "tracking-wide placeholder:normal-case placeholder:tracking-normal"
                : ""
            }`}
          />
          {!value && (
            <span
              className={`pointer-events-none mr-2 shrink-0 rounded-full px-2 py-0.5 text-[11px] ${
                onDark
                  ? "bg-white/10 text-neutral-400"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {t("creatorCode.optional")}
            </span>
          )}
        </label>
        <Button
          type="submit"
          disabled={!value.trim() || pending || locked}
          className={
            onDark
              ? "h-10 bg-indigo-500 text-white hover:bg-indigo-400"
              : "h-10"
          }
        >
          {pending ? <Loader2 className="h-4 w-4 animate-spin" /> : buttonLabel}
        </Button>
      </form>
      {message && (
        <p
          role={message.ok ? "status" : "alert"}
          className={`mt-2 text-xs ${message.ok ? "text-emerald-400" : "text-red-400"}`}
        >
          {message.text}
        </p>
      )}
    </div>
  );
}

/**
 * Optional creator-code input (EIGI, ...). Never required: the caller decides
 * where to offer it and what "skip" means. Rules live server-side in
 * server/creatorCodes.ts; this only shows the outcome.
 */
export default function CreatorCodeField({
  surface,
  autoFocus,
  onDark,
}: {
  surface: Exclude<CreatorCodeSurface, "mobile_profile">;
  autoFocus?: boolean;
  /** Survey dialog uses fixed dark colors instead of theme tokens. */
  onDark?: boolean;
}) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const redeem = trpc.creatorCode.redeem.useMutation();
  const [code, setCode] = useState("");
  const [message, setMessage] = useState<Message>(null);
  const [applied, setApplied] = useState(false);

  const submit = async () => {
    const typed = code.trim();
    if (!typed || redeem.isPending || applied) return;
    setMessage(null);
    try {
      const result = await redeem.mutateAsync({ code: typed, surface });
      setMessage({ ok: result.ok, text: creatorCodeResultText(t, result) });
      if (!result.ok) return;
      setApplied(true);
      void utils.creatorCode.status.invalidate();
      if (result.bonusCredits > 0) {
        void utils.payment.getSubscription.invalidate();
      }
    } catch {
      setMessage({ ok: false, text: t("creatorCode.error") });
    }
  };

  return (
    <OptionalInputRow
      value={code}
      onChange={(value) => {
        setCode(value);
        if (message && !message.ok) setMessage(null);
      }}
      placeholder={t("creatorCode.placeholder")}
      buttonLabel={t("creatorCode.apply")}
      pending={redeem.isPending}
      locked={applied}
      autoFocus={autoFocus}
      onDark={onDark}
      uppercase
      message={message}
      onSubmit={() => void submit()}
    />
  );
}

/**
 * Signup survey follow-up: "Who told you about Solmi?" — a name, @handle or
 * code, always optional. A creator code is applied; anything else is recorded
 * for attribution (server/creatorCodes.ts recordNamedReferrer).
 */
export function ReferrerNameField({
  source,
  onDone,
}: {
  /** The survey's "How did you hear about us?" answer. */
  source?: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const whoToldYou = trpc.creatorCode.whoToldYou.useMutation();
  const [text, setText] = useState("");
  const [message, setMessage] = useState<Message>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    const typed = text.trim();
    if (!typed || whoToldYou.isPending || done) return;
    setMessage(null);
    try {
      const answer = await whoToldYou.mutateAsync({ text: typed, source });
      if (answer.kind === "code") {
        const { result } = answer;
        setMessage({ ok: result.ok, text: creatorCodeResultText(t, result) });
        if (!result.ok) return;
        void utils.creatorCode.status.invalidate();
        if (result.bonusCredits > 0) {
          void utils.payment.getSubscription.invalidate();
        }
      } else {
        setMessage({ ok: true, text: t("creatorCode.thanks") });
      }
      setDone(true);
      window.setTimeout(onDone, 1600);
    } catch {
      setMessage({ ok: false, text: t("creatorCode.error") });
    }
  };

  return (
    <OptionalInputRow
      value={text}
      onChange={(value) => {
        setText(value);
        if (message && !message.ok) setMessage(null);
      }}
      placeholder={t("creatorCode.whoPlaceholder")}
      buttonLabel={t("creatorCode.done")}
      pending={whoToldYou.isPending}
      locked={done}
      autoFocus
      onDark
      message={message}
      onSubmit={() => void submit()}
    />
  );
}

/**
 * Collapsed "Have a creator code?" link that opens the field. Renders nothing
 * once the account can't take a code (already has one, or past the window).
 * Mount only for signed-in users.
 */
export function CreatorCodeLink({
  surface,
  className = "",
}: {
  surface: Exclude<CreatorCodeSurface, "mobile_profile">;
  className?: string;
}) {
  const { t } = useTranslation();
  const status = trpc.creatorCode.status.useQuery(undefined, {
    staleTime: 60_000,
  });
  const [expanded, setExpanded] = useState(false);
  if (expanded) {
    return (
      <div className={className}>
        <CreatorCodeField surface={surface} autoFocus />
      </div>
    );
  }
  if (!status.data?.canRedeem) return null;
  return (
    <button
      type="button"
      onClick={() => setExpanded(true)}
      className={`w-full text-center text-xs text-muted-foreground hover:text-foreground transition-colors ${className}`}
    >
      {t("creatorCode.haveCode")}
    </button>
  );
}
